import { useMemo } from 'react'
import moment from 'moment'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import getLoyaltyTierThresholds, { getTiersAbove, getTierThreshold } from 'loyalty/selectors/getLoyaltyTierThresholds'

interface TierProgressResult {
  nextTier: App.LoyaltyTier | undefined
  nextTierName: string | undefined
  creditsNeeded: number
  nightsNeeded: number
  statusReviewDate: string | undefined
  isLoading: boolean
  hasNextTier: boolean
}

/**
 * Hook to calculate the user's progress towards the next loyalty tier.
 * This is independent of any package - just shows what the user still needs.
 * Enhancement: This hook logic should be moved up to backend in the future
 */
function useUserTierProgress(): TierProgressResult {
  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const loyaltyAccountLoading = useAppSelector(
    state => state.loyalty.account.fetching || state.loyalty.account.initial,
  )
  const pendingPoints = useAppSelector(state => state.loyalty.accountPendingPoints.data)
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)

  const result = useMemo((): TierProgressResult => {
    if (!loyaltyAccount || loyaltyAccountLoading || !tierThresholds) {
      return {
        nextTier: undefined,
        nextTierName: undefined,
        creditsNeeded: 0,
        nightsNeeded: 0,
        statusReviewDate: undefined,
        isLoading: !!loyaltyAccountLoading || !tierThresholds,
        hasNextTier: false,
      }
    }

    const currentTier = loyaltyAccount.tier
    const tiersAbove = getTiersAbove(currentTier)

    if (tiersAbove.length === 0) {
      return {
        nextTier: undefined,
        nextTierName: undefined,
        creditsNeeded: 0,
        nightsNeeded: 0,
        statusReviewDate: loyaltyAccount.statusReviewDate,
        isLoading: false,
        hasNextTier: false,
      }
    }

    const nextTier = tiersAbove[0]
    const requirements = getTierThreshold(tierThresholds, nextTier)

    const currentStatusCredits = loyaltyAccount.statusCredits ?? 0
    const currentNights = loyaltyAccount.numberOfNights ?? 0

    let pendingStatusCredits = 0
    let pendingNights = 0

    if (loyaltyAccount.statusReviewDate && pendingPoints && pendingPoints.length > 0) {
      const membershipYearEnd = moment(loyaltyAccount.statusReviewDate)
      for (const pending of pendingPoints) {
        if (pending.pointsEarnDate) {
          const earnDate = moment(pending.pointsEarnDate)
          if (earnDate.isSameOrBefore(membershipYearEnd)) {
            pendingStatusCredits += pending.statusCredits
            pendingNights += pending.numNights ?? 0
          }
        }
      }
    }

    const totalCredits = currentStatusCredits + pendingStatusCredits
    const totalNights = currentNights + pendingNights

    const creditsNeeded = Math.max(0, requirements.statusCredits - totalCredits)
    const nightsNeeded = Math.max(0, requirements.nights - totalNights)

    return {
      nextTier,
      nextTierName: getLoyaltyTierDisplayName(nextTier),
      creditsNeeded,
      nightsNeeded,
      statusReviewDate: loyaltyAccount.statusReviewDate,
      isLoading: false,
      hasNextTier: true,
    }
  }, [loyaltyAccount, loyaltyAccountLoading, pendingPoints, tierThresholds])

  return result
}

export default useUserTierProgress
